import { isAdminSpaceUser } from '@/utils/accessControlUtils';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { jwtDecode } from 'jwt-decode';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // Check admin status from JWT token and localStorage user (avoiding useUserPermissions hook)
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    let adminStatus = false;
    let superAdminStatus = false;

    // Check if user is a partner (partners are treated as super admins by default)
    const isPartner = !!(
      user &&
      (user.role === 'client' ||
        user.role === 'partenaire' ||
        user.role === 'partner' ||
        user.userType === 'partner')
    );

    // Check superAdmin flag from localStorage user
    const hasSuperAdminFlag = !!(
      user &&
      (user.superAdmin === true || user.superAdmin === 1 || user.superAdmin === '1')
    );

    if (token) {
      try {
        const decoded = jwtDecode(token);
        // Legacy role-based check (for system admins)
        const isLegacyAdmin = isAdminSpaceUser(decoded);
        const isLegacySuperAdmin = decoded.role === 'Super Admin';

        // Partners are treated as super admins by default
        // OR users with superAdmin flag
        // OR legacy system admins
        adminStatus = isPartner || hasSuperAdminFlag || isLegacyAdmin;
        superAdminStatus = isPartner || hasSuperAdminFlag || isLegacySuperAdmin;
      } catch (error) {
        console.error('Error decoding token:', error);
        // Fallback to localStorage user check
        adminStatus = isPartner || hasSuperAdminFlag;
        superAdminStatus = isPartner || hasSuperAdminFlag;
      }
    } else {
      // No token, check localStorage user only
      adminStatus = isPartner || hasSuperAdminFlag;
      superAdminStatus = isPartner || hasSuperAdminFlag;
    }


    setIsAdmin(adminStatus);
    setIsSuperAdmin(superAdminStatus);
  }, []); // Empty dependency array - only run once on mount

  // Load notifications and pending requests count (API calls disabled - no backend endpoint)
  const loadNotifications = useCallback(async () => {
    setNotifications([]);
    setUnreadCount(0);
    setLoading(false);
  }, []);

  // Refresh notifications (public method)
  const refreshNotifications = useCallback(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Load notifications when admin status changes
  useEffect(() => {
    if (isAdmin) {
      loadNotifications();
    }
  }, [isAdmin, loadNotifications]);

  // Set up auto-refresh interval
  useEffect(() => {
    if (!isAdmin) return;

    // Only refresh if we have a token
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    const interval = setInterval(() => {
      // Double-check token still exists before refreshing
      if (localStorage.getItem('accessToken')) {
        loadNotifications();
      }
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [isAdmin, loadNotifications]);

  const value = {
    notifications,
    unreadCount,
    loading,
    isAdmin,
    isSuperAdmin,
    refreshNotifications,
    loadNotifications,
  };

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

export default NotificationContext;
